import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, formatTransactionError } from '../utils/transactionHelper';
import './AdminPanel.css';

const AdminPanel = () => {
  const web3Context = useWeb3();
  const { account, contract, web3, isAdmin, isOwner } = web3Context;

  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);

  // Platform Economics & Config
  const [tokenPrice, setTokenPrice] = useState('');
  const [minSellAmount, setMinSellAmount] = useState('');
  const [registerBonus, setRegisterBonus] = useState('');
  const [cooldownTime, setCooldownTime] = useState('');
  const [antiBotEnabled, setAntiBotEnabled] = useState(false);
  const [stakingEnabled, setStakingEnabled] = useState(false);

  // Offers
  const [offers, setOffers] = useState([]);
  const [newOffer, setNewOffer] = useState({
    index: '',
    minTokens: '',
    bonusPercent: '',
    feeBNB: '',
    active: true
  });

  // User Management
  const [userAddress, setUserAddress] = useState('');
  const [userStatus, setUserStatus] = useState(true);

  // Owner Controls
  const [newAdminAddress, setNewAdminAddress] = useState('');
  const [usdtAddressInput, setUsdtAddressInput] = useState('');
  const [tokenAddressInput, setTokenAddressInput] = useState('');

  // Withdrawals
  const [withdrawBNBAddress, setWithdrawBNBAddress] = useState('');
  const [withdrawTokenAddress, setWithdrawTokenAddress] = useState('');
  const [withdrawRecipient, setWithdrawRecipient] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  useEffect(() => {
    if (contract && isAdmin) {
      loadSettings();
    }
  }, [contract, isAdmin]);

  const loadSettings = async () => {
    try {
      const [price, minSell, bonus, cooldown, antiBot, staking] = await Promise.all([
        contract.methods.tokenPerUSDT().call(),
        contract.methods.minSellAmount().call(),
        contract.methods.registerBonus().call(),
        contract.methods.cooldownTime().call(),
        contract.methods.antiBotEnabled().call(),
        contract.methods.stakingEnabled().call()
      ]);

      setTokenPrice(price.toString());
      setMinSellAmount(web3.utils.fromWei(minSell.toString(), 'ether'));
      setRegisterBonus(web3.utils.fromWei(bonus.toString(), 'ether'));
      setCooldownTime(cooldown.toString());
      setAntiBotEnabled(antiBot);
      setStakingEnabled(staking);

      // Load contract offers
      const offersList = [];
      let i = 0;
      while (true) {
        try {
          const offer = await contract.methods.offers(i).call();
          if (!offer || offer.minTokens === undefined) break;

          offersList.push({
            index: i,
            minTokens: web3.utils.fromWei(offer.minTokens.toString(), 'ether'),
            bonusPercent: offer.bonusPercent.toString(),
            feeBNB: web3.utils.fromWei(offer.feeBNB.toString(), 'ether'),
            active: offer.active
          });
          i++;
        } catch {
          break;
        }
      }
      setOffers(offersList);
    } catch (error) {
      console.error('Error loading admin settings:', error);
    }
  };

  const handleSetTokenPrice = async () => {
    if (!tokenPrice || parseFloat(tokenPrice) <= 0) {
      alert('Please enter a valid price (tokens per 1 USDT)');
      return;
    }

    setLoading(true);
    try {
      const method = contract.methods.setTokenPrice(tokenPrice);
      await sendContractTransaction(web3Context, method);
      alert('Token price updated successfully!');
      loadSettings();
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSetMinSell = async () => {
    if (!minSellAmount || parseFloat(minSellAmount) <= 0) {
      alert('Please enter a valid min sell amount');
      return;
    }

    setLoading(true);
    try {
      const amountWei = web3.utils.toWei(minSellAmount, 'ether');
      const method = contract.methods.setMinSell(amountWei);
      await sendContractTransaction(web3Context, method);
      alert('Minimum sell amount updated!');
      loadSettings();
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSetRegisterBonus = async () => {
    setLoading(true);
    try {
      const bonusWei = web3.utils.toWei(registerBonus || '0', 'ether');
      const method = contract.methods.setRegisterBonus(bonusWei);
      await sendContractTransaction(web3Context, method);
      alert('Register bonus updated!');
      loadSettings();
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSetCooldown = async () => {
    if (!cooldownTime || parseInt(cooldownTime) < 0) {
      alert('Please enter valid cooldown seconds');
      return;
    }

    setLoading(true);
    try {
      const method = contract.methods.setCooldown(cooldownTime);
      await sendContractTransaction(web3Context, method);
      alert('Anti-bot cooldown time updated!');
      loadSettings();
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAntiBot = async () => {
    setLoading(true);
    try {
      const method = contract.methods.toggleAntiBot(!antiBotEnabled);
      await sendContractTransaction(web3Context, method);
      setAntiBotEnabled(!antiBotEnabled);
      alert(`Anti-bot is now ${!antiBotEnabled ? 'ENABLED' : 'DISABLED'}`);
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStaking = async () => {
    setLoading(true);
    try {
      const method = contract.methods.toggleStaking(!stakingEnabled);
      await sendContractTransaction(web3Context, method);
      setStakingEnabled(!stakingEnabled);
      alert(`Staking is now ${!stakingEnabled ? 'ENABLED' : 'DISABLED'}`);
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSetOffer = async (targetIndex) => {
    const idx = targetIndex !== undefined ? targetIndex : (newOffer.index !== '' ? parseInt(newOffer.index) : offers.length);
    if (!newOffer.minTokens || !newOffer.bonusPercent || !newOffer.feeBNB) {
      alert('Please fill out all offer parameters (Min Tokens, Bonus %, Fee BNB)');
      return;
    }

    setLoading(true);
    try {
      const minTokensWei = web3.utils.toWei(newOffer.minTokens, 'ether');
      const feeBNBWei = web3.utils.toWei(newOffer.feeBNB, 'ether');

      const method = contract.methods.setOffer(
        idx,
        minTokensWei,
        newOffer.bonusPercent,
        feeBNBWei,
        newOffer.active
      );

      await sendContractTransaction(web3Context, method);
      alert(`Offer Tier #${idx} configured successfully!`);
      setNewOffer({ index: '', minTokens: '', bonusPercent: '', feeBNB: '', active: true });
      loadSettings();
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSetUserStatus = async () => {
    if (!web3.utils.isAddress(userAddress)) {
      alert('Please enter a valid user Ethereum/BSC address');
      return;
    }

    setLoading(true);
    try {
      const method = contract.methods.setUserStatus(userAddress, userStatus);
      await sendContractTransaction(web3Context, method);
      alert(`User ${userAddress} status updated to ${userStatus ? 'ACTIVE' : 'INACTIVE'}`);
      setUserAddress('');
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!web3.utils.isAddress(newAdminAddress)) {
      alert('Please enter a valid Ethereum/BSC address for new admin');
      return;
    }

    setLoading(true);
    try {
      const method = contract.methods.addAdmin(newAdminAddress);
      await sendContractTransaction(web3Context, method);
      alert(`Address ${newAdminAddress} granted Admin permissions!`);
      setNewAdminAddress('');
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSetTokens = async () => {
    if (!web3.utils.isAddress(usdtAddressInput) || !web3.utils.isAddress(tokenAddressInput)) {
      alert('Please enter valid contract addresses for both USDT and Token');
      return;
    }

    setLoading(true);
    try {
      const method = contract.methods.setTokens(usdtAddressInput, tokenAddressInput);
      await sendContractTransaction(web3Context, method);
      alert('Contract USDT & Token addresses updated successfully!');
      setUsdtAddressInput('');
      setTokenAddressInput('');
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawBNB = async () => {
    if (!web3.utils.isAddress(withdrawBNBAddress)) {
      alert('Please enter a valid recipient address for BNB');
      return;
    }

    setLoading(true);
    try {
      const method = contract.methods.withdrawBNB(withdrawBNBAddress);
      await sendContractTransaction(web3Context, method);
      alert('All accumulated BNB fees withdrawn successfully!');
      setWithdrawBNBAddress('');
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawToken = async () => {
    if (!web3.utils.isAddress(withdrawTokenAddress) || !web3.utils.isAddress(withdrawRecipient) || !withdrawAmount) {
      alert('Please fill out all ERC20 withdrawal parameters');
      return;
    }

    setLoading(true);
    try {
      const amountWei = web3.utils.toWei(withdrawAmount, 'ether');
      const method = contract.methods.withdrawToken(withdrawTokenAddress, withdrawRecipient, amountWei);
      await sendContractTransaction(web3Context, method);
      alert(`Withdrew ${withdrawAmount} tokens to ${withdrawRecipient}!`);
      setWithdrawTokenAddress('');
      setWithdrawRecipient('');
      setWithdrawAmount('');
    } catch (error) {
      alert('Failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-wrapper">
        <div className="denied-box">
          <div className="denied-icon">🔒</div>
          <h2>Admin Access Restricted</h2>
          <p>Your connected wallet ({account ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'Not Connected'}) is not an owner or designated admin of the P2PTrade contract.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wrapper">
      <div className="admin-header-card">
        <div className="title-area">
          <h2>🛡️ Protocol Control Center</h2>
          <p>Full administrative access over market price, fee tiers, anti-bot rules, and treasury operations.</p>
        </div>
        <div className="badge-area">
          {isOwner && <span className="badge owner-tag">👑 Contract Owner</span>}
          <span className="badge admin-tag">⚡ Admin Active</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-nav-tabs">
        <button className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          ⚙️ General Settings
        </button>
        <button className={`nav-tab ${activeTab === 'offers' ? 'active' : ''}`} onClick={() => setActiveTab('offers')}>
          🎁 Offer Tiers
        </button>
        <button className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          👥 User Status
        </button>
        {isOwner && (
          <button className={`nav-tab ${activeTab === 'owner' ? 'active' : ''}`} onClick={() => setActiveTab('owner')}>
            🔑 Owner Controls
          </button>
        )}
        <button className={`nav-tab ${activeTab === 'treasury' ? 'active' : ''}`} onClick={() => setActiveTab('treasury')}>
          🏦 Treasury & Withdraw
        </button>
      </div>

      {/* General Settings Tab */}
      {activeTab === 'settings' && (
        <div className="admin-tab-content">
          <div className="control-grid">
            {/* Token Price */}
            <div className="control-card">
              <h4>Exchange Rate (Token per USDT)</h4>
              <p>Current: <strong>1 USDT = {tokenPrice} Tokens</strong></p>
              <div className="inline-form">
                <input
                  type="number"
                  placeholder="e.g. 100"
                  value={tokenPrice}
                  onChange={(e) => setTokenPrice(e.target.value)}
                  disabled={loading}
                />
                <button onClick={handleSetTokenPrice} disabled={loading}>Update Price</button>
              </div>
            </div>

            {/* Min Sell Amount */}
            <div className="control-card">
              <h4>Minimum Sell Amount (Tokens)</h4>
              <p>Current: <strong>{minSellAmount} Tokens</strong></p>
              <div className="inline-form">
                <input
                  type="number"
                  placeholder="e.g. 1000"
                  value={minSellAmount}
                  onChange={(e) => setMinSellAmount(e.target.value)}
                  disabled={loading}
                />
                <button onClick={handleSetMinSell} disabled={loading}>Update Min Sell</button>
              </div>
            </div>

            {/* Registration Bonus */}
            <div className="control-card">
              <h4>Registration Bonus (Tokens)</h4>
              <p>Current: <strong>{registerBonus} Tokens</strong></p>
              <div className="inline-form">
                <input
                  type="number"
                  placeholder="e.g. 50"
                  value={registerBonus}
                  onChange={(e) => setRegisterBonus(e.target.value)}
                  disabled={loading}
                />
                <button onClick={handleSetRegisterBonus} disabled={loading}>Update Bonus</button>
              </div>
            </div>

            {/* Cooldown Time */}
            <div className="control-card">
              <h4>Anti-Bot Cooldown (Seconds)</h4>
              <p>Current: <strong>{cooldownTime} Seconds</strong></p>
              <div className="inline-form">
                <input
                  type="number"
                  placeholder="e.g. 10"
                  value={cooldownTime}
                  onChange={(e) => setCooldownTime(e.target.value)}
                  disabled={loading}
                />
                <button onClick={handleSetCooldown} disabled={loading}>Update Cooldown</button>
              </div>
            </div>

            {/* Toggles */}
            <div className="control-card full-width">
              <h4>Security & Staking Feature Switches</h4>
              <div className="switches-row">
                <div className="switch-box">
                  <div className="switch-info">
                    <span className="switch-name">🤖 Anti-Bot Security Engine</span>
                    <span className="switch-desc">Enforces single tx/block & cooldown timestamp validation</span>
                  </div>
                  <button
                    className={`toggle-pill ${antiBotEnabled ? 'on' : 'off'}`}
                    onClick={handleToggleAntiBot}
                    disabled={loading}
                  >
                    {antiBotEnabled ? 'ACTIVE (ENABLED)' : 'DISABLED'}
                  </button>
                </div>

                <div className="switch-box">
                  <div className="switch-info">
                    <span className="switch-name">💎 Staking Program</span>
                    <span className="switch-desc">Enables/Disables new USDT deposits into staking pool</span>
                  </div>
                  <button
                    className={`toggle-pill ${stakingEnabled ? 'on' : 'off'}`}
                    onClick={handleToggleStaking}
                    disabled={loading}
                  >
                    {stakingEnabled ? 'ACTIVE (ENABLED)' : 'DISABLED'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offers Tab */}
      {activeTab === 'offers' && (
        <div className="admin-tab-content">
          <div className="offers-management-grid">
            <div className="existing-offers-card">
              <h3>Current Offer Tiers</h3>
              {offers.length === 0 ? (
                <p className="no-offers">No fee/bonus offer tiers created yet.</p>
              ) : (
                <div className="offers-table">
                  <div className="table-header">
                    <span>Index</span>
                    <span>Min Tokens</span>
                    <span>Bonus %</span>
                    <span>BNB Fee</span>
                    <span>Status</span>
                    <span>Action</span>
                  </div>
                  {offers.map((off) => (
                    <div key={off.index} className="table-row">
                      <span>#{off.index}</span>
                      <span>{off.minTokens}</span>
                      <span className="bonus-highlight">+{off.bonusPercent}%</span>
                      <span>{off.feeBNB} BNB</span>
                      <span className={`status-pill ${off.active ? 'active' : 'inactive'}`}>
                        {off.active ? 'Active' : 'Disabled'}
                      </span>
                      <button
                        className="edit-offer-btn"
                        onClick={() => setNewOffer({
                          index: off.index.toString(),
                          minTokens: off.minTokens,
                          bonusPercent: off.bonusPercent,
                          feeBNB: off.feeBNB,
                          active: off.active
                        })}
                      >
                        Load to Edit
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="create-offer-card">
              <h3>{newOffer.index !== '' ? `Edit Offer Tier #${newOffer.index}` : 'Add New Offer Tier'}</h3>
              <div className="offer-inputs">
                {newOffer.index !== '' && (
                  <div className="field-group">
                    <label>Target Offer Index:</label>
                    <input type="number" value={newOffer.index} readOnly />
                  </div>
                )}
                <div className="field-group">
                  <label>Min Tokens Threshold:</label>
                  <input
                    type="number"
                    placeholder="e.g. 1000"
                    value={newOffer.minTokens}
                    onChange={(e) => setNewOffer({ ...newOffer, minTokens: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <label>Bonus Percentage (%):</label>
                  <input
                    type="number"
                    placeholder="e.g. 5"
                    value={newOffer.bonusPercent}
                    onChange={(e) => setNewOffer({ ...newOffer, bonusPercent: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <label>Required Platform Fee (BNB):</label>
                  <input
                    type="number"
                    placeholder="e.g. 0.005"
                    value={newOffer.feeBNB}
                    onChange={(e) => setNewOffer({ ...newOffer, feeBNB: e.target.value })}
                  />
                </div>
                <div className="field-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newOffer.active}
                      onChange={(e) => setNewOffer({ ...newOffer, active: e.target.checked })}
                    />
                    Enable Tier Active Status
                  </label>
                </div>
                <button className="btn-save-offer" onClick={() => handleSetOffer()} disabled={loading}>
                  Save Offer Tier Config
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="admin-tab-content">
          <div className="user-mod-card">
            <h3>Account Moderation</h3>
            <p>Activate or deactivate user account trading and staking privileges.</p>

            <div className="mod-form">
              <div className="field">
                <label>User Address (0x...)</label>
                <input
                  type="text"
                  placeholder="0x1234..."
                  value={userAddress}
                  onChange={(e) => setUserAddress(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Status Action</label>
                <select value={userStatus.toString()} onChange={(e) => setUserStatus(e.target.value === 'true')}>
                  <option value="true">Active (Enable Trading)</option>
                  <option value="false">Inactive (Suspend Privileges)</option>
                </select>
              </div>
              <button className="btn-mod-submit" onClick={handleSetUserStatus} disabled={loading}>
                Apply User Status Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Owner Controls Tab */}
      {isOwner && activeTab === 'owner' && (
        <div className="admin-tab-content">
          <div className="owner-grid">
            <div className="owner-card">
              <h3>👑 Assign New Admin</h3>
              <p>Grant admin privileges to another wallet address.</p>
              <div className="field-row">
                <input
                  type="text"
                  placeholder="New Admin Address (0x...)"
                  value={newAdminAddress}
                  onChange={(e) => setNewAdminAddress(e.target.value)}
                />
                <button onClick={handleAddAdmin} disabled={loading}>Grant Admin Role</button>
              </div>
            </div>

            <div className="owner-card">
              <h3>🔄 Update Contract Token Addresses</h3>
              <p>Re-assign the USDT and Token ERC20 contract addresses.</p>
              <div className="field-col">
                <input
                  type="text"
                  placeholder="New USDT Address (0x...)"
                  value={usdtAddressInput}
                  onChange={(e) => setUsdtAddressInput(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="New Target Token Address (0x...)"
                  value={tokenAddressInput}
                  onChange={(e) => setTokenAddressInput(e.target.value)}
                />
                <button onClick={handleSetTokens} disabled={loading}>Update Contract Tokens</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Treasury Tab */}
      {activeTab === 'treasury' && (
        <div className="admin-tab-content">
          <div className="treasury-grid">
            <div className="treasury-card">
              <h3>💰 Withdraw Collected BNB Fees</h3>
              <p>Extract all accumulated BNB native platform fees from sell/buy order fees.</p>
              <div className="field-col">
                <input
                  type="text"
                  placeholder="BNB Recipient Wallet (0x...)"
                  value={withdrawBNBAddress}
                  onChange={(e) => setWithdrawBNBAddress(e.target.value)}
                />
                <button className="btn-withdraw-bnb" onClick={handleWithdrawBNB} disabled={loading}>
                  Withdraw All BNB Balance
                </button>
              </div>
            </div>

            <div className="treasury-card">
              <h3>🪙 Emergency ERC20 Token Withdrawal</h3>
              <p>Extract any ERC20 tokens held by the contract to a target wallet.</p>
              <div className="field-col">
                <input
                  type="text"
                  placeholder="ERC20 Token Address (0x...)"
                  value={withdrawTokenAddress}
                  onChange={(e) => setWithdrawTokenAddress(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Recipient Wallet (0x...)"
                  value={withdrawRecipient}
                  onChange={(e) => setWithdrawRecipient(e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Token Amount (e.g. 5000)"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                />
                <button className="btn-withdraw-token" onClick={handleWithdrawToken} disabled={loading}>
                  Withdraw ERC20 Tokens
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;